/**
 * FENLUX product landing — purchase / sticky / modal behaviour.
 * Visual CSS lives in fenlux-product-system.css.
 * Runtime behaviour is equivalent to the previous inline section script.
 */
(function () {
  const CONTROLLERS = (window.__fenluxProductLandingControllers =
    window.__fenluxProductLandingControllers || {});

  function readConfig(section) {
    const configEl = section.querySelector("[data-fenlux-product-landing-config]");
    if (!configEl) return null;

    try {
      return JSON.parse(configEl.textContent);
    } catch (error) {
      return null;
    }
  }

  function destroy(sectionId) {
    const controller = CONTROLLERS[sectionId];
    if (!controller) return;
    if (typeof controller.cleanup === "function") controller.cleanup();
    delete CONTROLLERS[sectionId];
  }

  function initSection(section) {
    if (!section) return;

    const config = readConfig(section);
    if (!config || !config.sectionId) return;

    destroy(config.sectionId);

    const abort = new AbortController();
    const { signal } = abort;
    const cleanups = [];

    const addListener = (target, type, handler, options) => {
      target.addEventListener(type, handler, Object.assign({ signal }, options || {}));
    };

    const modal = section.querySelector("[data-fenlux-driveway-scenario-modal]");
    const optionGuideModal = section.querySelector("[data-fenlux-option-guide-modal]");
    const priceTargets = section.querySelectorAll("[data-fenlux-driveway-price]");
    const variants = config.variants || [];
    const moneyFormat = config.moneyFormat;
    const currency = config.currency || "AUD";

    /* ------------------------------------------------------------------ */
    /* Price rendering                                                    */
    /* ------------------------------------------------------------------ */

    const formatMoney = (cents) => {
      if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
        return window.Shopify.formatMoney(cents, moneyFormat);
      }

      return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: currency,
      }).format(cents / 100);
    };

    const getVariantData = (variant) => {
      if (!variant) return null;

      return (
        variants.find(
          (item) => Number(item.id) === Number(variant.id)
        ) || null
      );
    };

    const renderHeroPriceHtml = (variant) => {
      const compareAt = Number(variant.compare_at_price);
      const price = Number(variant.price);

      if (compareAt > price) {
        const savePercent = Math.round(((compareAt - price) / compareAt) * 100);

        return [
          `<span class="fenlux-driveway-hero-price__compare">${formatMoney(compareAt)}</span>`,
          `<span class="fenlux-driveway-hero-price__current">${formatMoney(price)}</span>`,
          `<span class="fenlux-driveway-hero-save-badge">SAVE ${savePercent}%</span>`,
        ].join("");
      }

      return `<span class="fenlux-driveway-hero-price__current">${formatMoney(price)}</span>`;
    };

    const updateTopPrice = (variant) => {
      const variantData = getVariantData(variant);
      if (!priceTargets.length || !variantData) return;

      const priceHtml = renderHeroPriceHtml(variantData);
      priceTargets.forEach((priceTarget) => {
        priceTarget.innerHTML = priceHtml;
      });
    };

    const updatePriceFromForm = () => {
      const variantInput = section.querySelector('form[action*="/cart/add"] [name="id"]');
      if (!variantInput) return;
      const variant = variants.find((item) => Number(item.id) === Number(variantInput.value));
      updateTopPrice(variant);
    };

    addListener(section, "change", () => {
      window.setTimeout(updatePriceFromForm, 0);
    });

    addListener(document, "variant:change", (event) => {
      if (event.detail && event.detail.variant) updateTopPrice(event.detail.variant);
    });

    addListener(document, "product:variant-change", (event) => {
      if (event.detail && event.detail.variant) updateTopPrice(event.detail.variant);
    });

    if (priceTargets.length) {
      const variantInput = section.querySelector('form[action*="/cart/add"] [name="id"]');
      if (variantInput) {
        updatePriceFromForm();
      } else {
        const defaultVariant = variants.find((item) => item.available) || variants[0];
        if (defaultVariant) updateTopPrice(defaultVariant);
      }
    }

    /* ------------------------------------------------------------------ */
    /* Easify additional price detection                                  */
    /* ------------------------------------------------------------------ */

    const stickyBar = section.querySelector("[data-fenlux-driveway-sticky-atc]");
    const stickyPrice = section.querySelector("[data-fenlux-driveway-sticky-price]");
    const originalAddButton = section.querySelector(
      '[data-fenlux-driveway-buy-stack] button[name="add"]'
    );
    const mainBuyStack = section.querySelector("[data-fenlux-driveway-buy-stack]");
    const productInfo = config.productInfoId
      ? section.querySelector("#" + config.productInfoId)
      : section.querySelector('[id^="ProductInfo-"]');

    const getEasifyOptionsChargeLabel = () => {
      if (!productInfo) return null;

      const chargePatterns = [
        /additional charge of\s*(\$[\d,]+(?:\.\d{2})?|[£€][\d,]+(?:\.\d{2})?)/i,
        /additional price(?:\s*of)?\s*(\$[\d,]+(?:\.\d{2})?|[£€][\d,]+(?:\.\d{2})?)/i,
        /total additional price(?:\s*of)?\s*(\$[\d,]+(?:\.\d{2})?|[£€][\d,]+(?:\.\d{2})?)/i,
      ];

      const easifyScopes = productInfo.querySelectorAll(
        '[class*="easify"], [id*="easify"], [class*="Easify"], shopify-app-block'
      );
      const searchRoots = easifyScopes.length ? easifyScopes : [productInfo];

      let bestCharge = null;
      let bestTextLength = Infinity;

      searchRoots.forEach((root) => {
        root.querySelectorAll("*").forEach((el) => {
          if (stickyBar && stickyBar.contains(el)) return;

          const text = (el.textContent || "").replace(/\s+/g, " ").trim();
          if (!text || !/additional charge|additional price|total additional price/i.test(text)) return;

          for (const pattern of chargePatterns) {
            const match = text.match(pattern);
            if (!match) continue;

            const chargeLabel = match[1].trim();
            if (/^[£€$]?0(?:\.00)?$/.test(chargeLabel.replace(/,/g, ""))) return;

            if (text.length < bestTextLength) {
              bestTextLength = text.length;
              bestCharge = chargeLabel;
            }
            break;
          }
        });
      });

      return bestCharge;
    };

    const parseMoneyToCents = (moneyString) => {
      if (!moneyString) return null;

      const normalized = moneyString.replace(/[^\d.,]/g, "").replace(/,/g, "");
      const value = Number.parseFloat(normalized);

      if (Number.isNaN(value)) return null;

      return Math.round(value * 100);
    };

    /* ------------------------------------------------------------------ */
    /* Sticky ATC                                                         */
    /* ------------------------------------------------------------------ */

    const updateStickyPriceDisplay = (variant) => {
      if (!stickyPrice || !variant) return;

      const basePrice = formatMoney(variant.price);
      const optionsChargeLabel = getEasifyOptionsChargeLabel();

      if (!optionsChargeLabel) {
        stickyPrice.textContent = basePrice;
        return;
      }

      const chargeCents = parseMoneyToCents(optionsChargeLabel);

      if (chargeCents == null) {
        stickyPrice.textContent = basePrice;
        return;
      }

      stickyPrice.textContent = `${basePrice} + ${optionsChargeLabel} = ${formatMoney(
        variant.price + chargeCents
      )}`;
    };

    const updateStickyVariant = (variant) => {
      if (!stickyBar || !variant) return;
      updateStickyPriceDisplay(variant);
    };

    const syncStickyFromMain = () => {
      const variantInput = section.querySelector('form[action*="/cart/add"] [name="id"]');
      if (variantInput) {
        const variant = variants.find((item) => Number(item.id) === Number(variantInput.value));
        if (variant) updateStickyVariant(variant);
      }
    };

    const updateStickyVisibility = () => {
      if (!stickyBar || !mainBuyStack || !originalAddButton) return;

      const buyStackRect = mainBuyStack.getBoundingClientRect();
      const shouldShow = buyStackRect.bottom < 0;

      stickyBar.hidden = !shouldShow;
      stickyBar.classList.toggle("is-visible", shouldShow);
      stickyBar.setAttribute("aria-hidden", shouldShow ? "false" : "true");

      originalAddButton.classList.toggle("fenlux-original-atc--fixed", shouldShow);
    };

    if (stickyBar) {
      const handleStickyVariantChange = (event) => {
        if (event.detail && event.detail.variant) updateStickyVariant(event.detail.variant);
        window.setTimeout(syncStickyFromMain, 0);
      };

      addListener(document, "variant:change", handleStickyVariantChange);
      addListener(document, "product:variant-change", handleStickyVariantChange);

      addListener(section, "change", () => {
        window.setTimeout(syncStickyFromMain, 0);
      });

      if (productInfo && "MutationObserver" in window) {
        let easifyPriceSyncTimer;

        const scheduleEasifyPriceSync = () => {
          window.clearTimeout(easifyPriceSyncTimer);
          easifyPriceSyncTimer = window.setTimeout(() => {
            const variantInput = section.querySelector('form[action*="/cart/add"] [name="id"]');
            if (!variantInput) return;
            const variant = variants.find((item) => Number(item.id) === Number(variantInput.value));
            if (variant) updateStickyPriceDisplay(variant);
          }, 50);
        };

        const easifyPriceObserver = new MutationObserver(scheduleEasifyPriceSync);
        easifyPriceObserver.observe(productInfo, {
          childList: true,
          subtree: true,
          characterData: true,
        });

        cleanups.push(() => {
          window.clearTimeout(easifyPriceSyncTimer);
          easifyPriceObserver.disconnect();
        });
      }

      addListener(window, "scroll", updateStickyVisibility, { passive: true });
      addListener(window, "resize", () => {
        updateStickyVisibility();
        syncStickyFromMain();
      });

      syncStickyFromMain();
      updateStickyVisibility();
    }

    /* ------------------------------------------------------------------ */
    /* Pickup placement                                                   */
    /* ------------------------------------------------------------------ */

    const movePickupAfterDelivery = () => {
      const deliveryInfo = section.querySelector("[data-fenlux-driveway-delivery-info]");
      const pickupCandidate = section.querySelector(
        "pickup-availability, .pickup-availability, [data-pickup-availability]"
      );
      if (!deliveryInfo || !pickupCandidate || deliveryInfo.contains(pickupCandidate)) return;

      const pickupNode =
        pickupCandidate.closest(".pickup-availability-wrapper") ||
        pickupCandidate.closest("pickup-availability") ||
        pickupCandidate;
      if (pickupNode !== deliveryInfo.nextElementSibling) {
        deliveryInfo.insertAdjacentElement("afterend", pickupNode);
      }
    };

    movePickupAfterDelivery();
    const pickupTimer = window.setTimeout(movePickupAfterDelivery, 500);
    cleanups.push(() => window.clearTimeout(pickupTimer));

    /* ------------------------------------------------------------------ */
    /* Scenario / Site Check modal                                        */
    /* ------------------------------------------------------------------ */

    const openModal = () => {
      if (!modal) return;
      modal.hidden = false;
      modal.classList.add("is-active");
      document.documentElement.classList.add("fenlux-driveway-modal-open");
      const dialog = modal.querySelector('[role="dialog"]');
      if (dialog) dialog.focus();
    };

    const closeModal = () => {
      if (!modal) return;
      modal.hidden = true;
      modal.classList.remove("is-active");
      document.documentElement.classList.remove("fenlux-driveway-modal-open");
    };

    addListener(section, "click", (event) => {
      if (!modal) return;

      const opener = event.target.closest("[data-fenlux-driveway-scenario-open]");
      if (opener) {
        event.preventDefault();
        openModal();
        return;
      }

      const scenarioTab = event.target.closest("[data-fenlux-driveway-scenario-option]");
      if (scenarioTab) {
        const scenario = scenarioTab.getAttribute("data-fenlux-driveway-scenario-option");
        modal.querySelectorAll("[data-fenlux-driveway-scenario-option]").forEach((tab) => {
          const isActive = tab === scenarioTab;
          tab.classList.toggle("is-active", isActive);
          tab.setAttribute("aria-selected", isActive ? "true" : "false");
        });
        modal.querySelectorAll("[data-fenlux-driveway-scenario-panel]").forEach((panel) => {
          const isActive = panel.getAttribute("data-fenlux-driveway-scenario-panel") === scenario;
          panel.hidden = !isActive;
          panel.classList.toggle("is-active", isActive);
        });
        return;
      }

      const closer = event.target.closest("[data-fenlux-driveway-scenario-close]");
      if (closer) {
        event.preventDefault();
        closeModal();
      }
    });

    addListener(document, "keydown", (event) => {
      if (!modal) return;
      if (event.key !== "Escape") return;
      if (!modal.hidden) closeModal();
    });

    /* ------------------------------------------------------------------ */
    /* Product option guide modal                                         */
    /* ------------------------------------------------------------------ */

    let optionGuideLastTrigger = null;

    const openOptionGuideModal = (trigger) => {
      if (!optionGuideModal) return;

      optionGuideLastTrigger = trigger || null;
      optionGuideModal.hidden = false;
      optionGuideModal.classList.add("is-active");
      document.documentElement.classList.add("fenlux-driveway-modal-open");

      const dialog = optionGuideModal.querySelector('[role="dialog"]');
      if (dialog) dialog.focus();
    };

    const closeOptionGuideModal = () => {
      if (!optionGuideModal) return;

      optionGuideModal.hidden = true;
      optionGuideModal.classList.remove("is-active");

      optionGuideModal.querySelectorAll("video").forEach((video) => {
        video.pause();
      });

      if (!modal || modal.hidden) {
        document.documentElement.classList.remove("fenlux-driveway-modal-open");
      }

      if (optionGuideLastTrigger && typeof optionGuideLastTrigger.focus === "function") {
        optionGuideLastTrigger.focus();
      }
      optionGuideLastTrigger = null;
    };

    addListener(section, "click", (event) => {
      if (!optionGuideModal) return;

      const opener = event.target.closest("[data-fenlux-option-guide-open]");
      if (opener) {
        event.preventDefault();
        event.stopPropagation();
        openOptionGuideModal(opener);
        return;
      }

      const closer = event.target.closest("[data-fenlux-option-guide-close]");
      if (closer) {
        event.preventDefault();
        closeOptionGuideModal();
      }
    });

    addListener(document, "keydown", (event) => {
      if (!optionGuideModal) return;
      if (event.key !== "Escape") return;
      if (!optionGuideModal.hidden) closeOptionGuideModal();
    });

    CONTROLLERS[config.sectionId] = {
      cleanup: () => {
        abort.abort();
        cleanups.forEach((fn) => fn());
        if (originalAddButton) {
          originalAddButton.classList.remove("fenlux-original-atc--fixed");
        }
        document.documentElement.classList.remove("fenlux-driveway-modal-open");
      },
    };
  }

  function boot(root) {
    const scope = root || document;
    const configs = scope.querySelectorAll
      ? scope.querySelectorAll("[data-fenlux-product-landing-config]")
      : [];

    configs.forEach((configEl) => {
      const section =
        configEl.closest("[id^='shopify-section-']") ||
        configEl.parentElement;
      initSection(section);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => boot());
  } else {
    boot();
  }

  document.addEventListener("shopify:section:load", (event) => {
    boot(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    const configEl = event.target.querySelector
      ? event.target.querySelector("[data-fenlux-product-landing-config]")
      : null;
    if (!configEl) return;
    try {
      const config = JSON.parse(configEl.textContent);
      if (config && config.sectionId) destroy(config.sectionId);
    } catch (error) {
      /* ignore */
    }
  });
})();
